/**
 * The strip that stays.
 *
 * Two things used to exist only on the share screen, and the share screen only exists until the
 * first person joins: **the link** and **the clock**. So a room could invite exactly one person,
 * and a session could run out at minute sixty while everybody was walking, with no warning
 * anywhere and no way to add an hour. Both were unreachable precisely when they mattered.
 *
 * They are chrome now, not a screen — one small row above whatever the session is showing, the
 * arrow included. It is deliberately quiet: a code, a clock, and a way to invite somebody. It
 * only raises its voice when the session is about to end, which is the one moment it should.
 */

import { el } from '../lib/dom';
import type { SessionState } from '../lib/session-model';
import { otherMembers } from '../lib/session-model';

export interface SessionBarOptions {
  code: string;
  /** Open the invite sheet. */
  onInvite: () => void;
  /** Add an hour. Resolves to false when the server refused, so the bar can say so. */
  onExtend: () => Promise<boolean>;
}

export interface SessionBar {
  element: HTMLElement;
  update: (state: SessionState) => void;
  destroy: () => void;
}

/** Below this the session is close enough to the end that it has to say so out loud. */
const WARN_MS = 10 * 60_000;

const pad = (value: number): string => String(value).padStart(2, '0');

/** Time left, as a readout rather than a promise: "58:12". */
export function formatRemaining(ms: number): string {
  if (ms <= 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${pad(minutes)}:${pad(totalSeconds % 60)}`;
  // Past an hour the seconds are noise — a weekend room counting down in seconds is absurd.
  return `${Math.floor(minutes / 60)}h ${pad(minutes % 60)}m`;
}

export function createSessionBar(options: SessionBarOptions): SessionBar {
  let state: SessionState | null = null;
  let extending = false;

  const codeChip = el('span', { class: 'bar__code mono', text: options.code });
  const here = el('span', { class: 'bar__here' });
  const clock = el('span', { class: 'bar__clock mono', text: '—' });

  const inviteButton = el(
    'button',
    { class: 'bar__invite', type: 'button', 'data-action': 'invite' },
    'Invite',
  );
  inviteButton.addEventListener('click', () => options.onInvite());

  /* --- the warning, which only appears when it is true -------------------- */

  const warningText = el('p', { class: 'bar__warning-text' });
  const extendButton = el(
    'button',
    { class: 'button button--small button--primary', type: 'button', 'data-action': 'extend' },
    'Add an hour',
  );

  extendButton.addEventListener('click', () => {
    if (extending) return;
    extending = true;
    extendButton.setAttribute('disabled', '');
    extendButton.textContent = 'Adding…';

    void options.onExtend().then((ok) => {
      extending = false;
      extendButton.removeAttribute('disabled');
      extendButton.textContent = ok ? 'Add an hour' : 'Could not — retry';
      render();
    });
  });

  const warning = el('div', { class: 'bar__warning', hidden: true }, warningText, extendButton);

  const element = el(
    'div',
    { class: 'bar' },
    el('div', { class: 'bar__row' }, codeChip, here, clock, inviteButton),
    warning,
  );

  const render = (): void => {
    const remaining = state?.expiresAt == null ? null : state.expiresAt - Date.now();

    clock.textContent = remaining === null ? '—' : formatRemaining(remaining);
    clock.classList.toggle('bar__clock--low', remaining !== null && remaining < WARN_MS);

    const others = state ? otherMembers(state).filter((member) => member.present).length : 0;
    here.textContent = others === 0 ? 'nobody yet' : `${others + 1} here`;

    // Only room enough for one of the two on a narrow phone, and "who is here" matters more
    // than the code once anybody has arrived.
    codeChip.hidden = others > 0;

    const closing = remaining !== null && remaining < WARN_MS && remaining > 0;
    warning.hidden = !closing;
    if (closing) {
      const canExtend = (state?.extensionsLeft ?? 0) > 0;
      warningText.textContent = canExtend
        ? `This session ends in ${formatRemaining(remaining)}.`
        : `Ends in ${formatRemaining(remaining)} — no extensions left.`;
      extendButton.hidden = !canExtend;
    }
  };

  render();
  const timer = window.setInterval(render, 1000);

  return {
    element,
    update: (next) => {
      state = next;
      render();
    },
    destroy: () => {
      window.clearInterval(timer);
      element.remove();
    },
  };
}
